import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, formatMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Anchor, ReceiptText, Trash2, FileText } from "lucide-react";

export default function Invoices() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const params = filter === "all" ? {} : { invoice_type: filter };
      const { data } = await api.get("/invoices", { params });
      setList(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const remove = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("Failed to fetch PDF");
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Billing</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Invoices</h1>
          <p className="text-sm text-zinc-500 mt-1">Freight and service invoices, generated with multi-currency support.</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate("/invoices/new?type=shipping")}
            data-testid="new-shipping-invoice"
            className="btn-pressable rounded-sm h-11 bg-[#09090B] hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs px-5"
          >
            <Anchor className="h-4 w-4 mr-2" /> New Freight Invoice
          </Button>
          <Button
            onClick={() => navigate("/invoices/new?type=service")}
            data-testid="new-service-invoice"
            className="btn-pressable rounded-sm h-11 bg-[#F97316] hover:bg-[#EA580C] text-white font-mono uppercase tracking-widest text-xs px-5"
          >
            <ReceiptText className="h-4 w-4 mr-2" /> New Service Invoice
          </Button>
        </div>
      </div>

      <div className="flex gap-2">
        {[
          ["all", "All"],
          ["shipping", "Freight"],
          ["service", "Service"],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`inv-filter-${k}`}
            className={`px-4 py-1.5 rounded-sm font-mono text-[10px] uppercase tracking-widest border ${
              filter === k
                ? "bg-[#09090B] text-white border-[#09090B]"
                : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50 hover:bg-zinc-50">
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Invoice</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Type</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Consignee</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Issued</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Due</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-right">Total</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Status</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-16 text-sm text-zinc-500">
                  <ReceiptText className="h-8 w-8 mx-auto mb-3 text-zinc-300" />
                  No invoices yet.
                </TableCell>
              </TableRow>
            )}
            {list.map((inv) => (
              <TableRow key={inv.id} className="hover:bg-zinc-50" data-testid={`inv-row-${inv.id}`}>
                <TableCell>
                  <Link to={`/invoices/${inv.id}`} className="font-mono text-xs text-zinc-900 hover:underline">
                    {inv.invoice_no}
                  </Link>
                </TableCell>
                <TableCell>
                  {inv.invoice_type === "shipping" ? (
                    <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-zinc-900 text-white rounded-sm">
                      <Anchor className="h-3 w-3 mr-1 inline" /> Freight
                    </span>
                  ) : (
                    <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-zinc-100 text-zinc-700 rounded-sm">
                      Service
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-sm">{inv.consignee_name}</TableCell>
                <TableCell className="font-mono text-xs">{inv.issue_date}</TableCell>
                <TableCell className="font-mono text-xs">{inv.due_date}</TableCell>
                <TableCell className="text-right font-mono text-sm font-semibold">
                  {formatMoney(inv.grand_total, inv.currency)}
                </TableCell>
                <TableCell>
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm ${
                    inv.status === "paid" ? "bg-emerald-100 text-emerald-700" :
                    inv.status === "sent" ? "bg-sky-100 text-sky-700" :
                    inv.status === "overdue" ? "bg-red-100 text-red-700" :
                    "bg-zinc-100 text-zinc-600"
                  }`}>
                    {inv.status}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => downloadPdf(inv.id)} data-testid={`inv-pdf-${inv.id}`}>
                    <FileText className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(inv.id)} data-testid={`inv-delete-${inv.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
